import { useCallback, useEffect, useId, useState } from 'react'
import type { JSX } from 'react'
import { useLocation } from 'react-router-dom'
import { clsx } from 'clsx'

import {
  ChevronDownIcon,
  ChevronRightIcon,
  Sidebar,
  useSidebar,
} from '@buffer-mono/popcorn'

import {
  useCurrentOrganization,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'
import type { Service } from './useChannels'
import { channelServices } from '~publish/helpers/channels'
import { useStartPageSunsetAnnouncementEnabled } from '~publish/hooks/useStartPageSunsetAnnouncementEnabled'

import { useTemporaryFlag } from '../../hooks/useTemporaryFlag'
import { useSavedViewsQuery } from '../SavedViewsSection/hooks'
import { SidebarErrorState } from '../SidebarErrorState'
import { ChannelGroupActions, SearchButton } from './ChannelGroupActions'
import { ChannelItem } from './ChannelItem'
import { ChannelsSectionSkeleton } from './ChannelsSectionSkeleton'
import { useLastChannelsCount } from './useLastChannelsCount'
import { ChannelSuggestions } from './ChannelSuggestions'
import {
  DraggableChannelItem,
  DraggableChannelsProvider,
  useDraggableChannelsContext,
  useOrderChannelMutation,
} from './DraggableChannelsSection'
import type { MainSidebarChannel } from './useChannels'
import { useChannels } from './useChannels'

import styles from './ChannelsSection.module.css'

/**
 * Extract channelId from pathname if it's a channel-specific route.
 * Returns null if it's an aggregated view (All Channels).
 */
function getChannelIdFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/(channels|comments|insights)\/([^/]+)/)
  return match?.[2] ?? null
}

const getChannelSuggestions = (
  channels: MainSidebarChannel[],
  onboardingServices: Service[] | null,
  isStartPageSunset: boolean,
): Service[] | null => {
  if (channels.length > 3) {
    return null
  }

  const connected = new Set(channels.map((ch) => ch.service))
  // Once the sunset is announced, Start Page is never suggested.
  const isSuggestable = (s: Service): boolean =>
    !connected.has(s) && !(isStartPageSunset && s === 'startPage')

  if (onboardingServices && onboardingServices.length > 0) {
    const priority = onboardingServices.filter(isSuggestable)
    const prioritySet = new Set(priority)
    const fill = channelServices.filter(
      (s) => isSuggestable(s) && !prioritySet.has(s),
    )
    return [...priority, ...fill]
  }

  return channelServices.filter(isSuggestable)
}

/**
 * Below this many channels the whole list fits at a glance, so the section
 * shows neither the channel count nor the search button.
 */
const LONG_CHANNEL_LIST_LENGTH = 10

export function ChannelsSection(): JSX.Element | null {
  const { channels, loading, error, onboardingChannelFocus } = useChannels()
  const { state } = useSidebar()
  const canConnectChannel = useHasOrganizationAction('manageChannels')
  const { savedViews } = useSavedViewsQuery()
  const [, setLastChannelsCount] = useLastChannelsCount()

  // Remember the loaded channel count so the next page load can size the
  // loading skeleton to this section's real geometry.
  useEffect(() => {
    if (loading || error) return
    setLastChannelsCount(channels.length)
  }, [loading, error, channels.length, setLastChannelsCount])

  // Only allow collapsing the Channels section when saved views exist — the
  // chevron is a space-management affordance that is useless on its own.
  const hasSavedViews = savedViews.length > 0
  const [isExpanded, setIsExpanded] = useState(true)
  const contentId = useId()

  const isStartPageSunset = useStartPageSunsetAnnouncementEnabled()

  const channelConnectionSuggestions = getChannelSuggestions(
    channels,
    onboardingChannelFocus,
    isStartPageSunset,
  )

  const hasSingleChannel = channels.length === 1

  // Don't show the "Channels (0)" label when there are no channels
  const hasNoChannels = channels.length === 0
  const shouldShowConnectChannelsLabel =
    channels.length >= 1 && channels.length <= 3

  if (!canConnectChannel && hasNoChannels) {
    return null
  }

  const isLongChannelList = channels.length >= LONG_CHANNEL_LIST_LENGTH
  const channelsLabel = hasNoChannels
    ? 'Connect channels'
    : isLongChannelList
      ? `Channels · ${channels.length}`
      : 'Channels'

  const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon
  // Keep content in the DOM when the section is collapsible so `aria-controls`
  // always references a real element; toggle visibility via `hidden`.
  const contentHidden = hasSavedViews && !isExpanded

  return (
    <Sidebar.Group className={styles.channelsSection}>
      <Sidebar.Separator className={styles.separator} />
      <Sidebar.GroupLabel
        className={clsx(
          styles.groupLabel,
          hasNoChannels && styles.connectChannelsLabel,
        )}
      >
        <div className={styles.channelTitleLabel}>
          {hasSavedViews ? (
            <button
              type="button"
              className={styles.collapseToggle}
              onClick={(): void => setIsExpanded((prev) => !prev)}
              aria-expanded={isExpanded}
              aria-controls={contentId}
            >
              <span>{channelsLabel}</span>
              <ChevronIcon
                size="xsmall"
                aria-hidden
                className={styles.chevron}
              />
            </button>
          ) : (
            <span>{channelsLabel}</span>
          )}
          <ChannelGroupActions>
            {isLongChannelList && <SearchButton />}
          </ChannelGroupActions>
        </div>
      </Sidebar.GroupLabel>
      <Sidebar.GroupContent
        id={contentId}
        className={styles.channelsGroupContent}
        hidden={contentHidden}
      >
        {error && (
          <SidebarErrorState heading="Failed to load channels" error={error} />
        )}
        {loading && !error && <ChannelsSectionSkeleton />}
        {!loading && !error && (
          <>
            {!hasNoChannels && (
              <ChannelsList
                key={hasSingleChannel ? channels[0]?.id : 'multi-channel'}
                channels={channels ?? []}
                defaultExpandedChannelId={
                  hasSingleChannel ? channels[0]?.id : undefined
                }
              />
            )}
            {canConnectChannel &&
            state === 'expanded' &&
            channelConnectionSuggestions ? (
              <span className={styles.suggestionsGroup}>
                {shouldShowConnectChannelsLabel ? (
                  <Sidebar.GroupLabel
                    className={styles.connectMoreChannelsLabel}
                  >
                    Connect more channels
                  </Sidebar.GroupLabel>
                ) : null}
                <ChannelSuggestions services={channelConnectionSuggestions} />
              </span>
            ) : null}
          </>
        )}
      </Sidebar.GroupContent>
    </Sidebar.Group>
  )
}

/**
 * Renders the list of channels with drag-and-drop reordering functionality.
 * Conditionally enables dragging only when there are 2 or more channels.
 * Manages expand/collapse state for all channels.
 */
function ChannelsList({
  channels,
  defaultExpandedChannelId,
}: {
  channels: ReturnType<typeof useChannels>['channels']
  defaultExpandedChannelId?: string
}): JSX.Element {
  const location = useLocation()
  const currentOrganization = useCurrentOrganization()
  const { orderChannel } = useOrderChannelMutation()

  // Manual expansions via chevron toggle - persist when navigating away
  const [manuallyExpandedIds, setManuallyExpandedIds] = useState<Set<string>>(
    () => new Set(defaultExpandedChannelId ? [defaultExpandedChannelId] : []),
  )
  // Navigation expansion - auto-collapses when navigating to different route
  const [navigationExpandedId, setNavigationExpandedId] = useState<
    string | null
  >(null)

  const [isNavigating, setNavigating] = useTemporaryFlag(50)

  // Compute if a channel is expanded (either manually or via navigation)
  const isChannelExpanded = useCallback(
    (channelId: string): boolean => {
      return (
        manuallyExpandedIds.has(channelId) || navigationExpandedId === channelId
      )
    },
    [manuallyExpandedIds, navigationExpandedId],
  )

  // Sync expansion state with URL changes
  useEffect(() => {
    const channelId = getChannelIdFromPathname(location.pathname)

    if (channelId && channels.some((ch) => ch.id === channelId)) {
      // Navigated to a channel-specific route
      setNavigating()
      setNavigationExpandedId(channelId)
    } else {
      // Navigated to an aggregated view - clear navigation expansion
      setNavigating()
      setNavigationExpandedId(null)
    }
  }, [location.pathname, channels, setNavigating])

  const isDraggingEnabled = channels.length > 1

  const handleNavigation = (channelId: string): void => {
    // Navigation click: set this channel as the navigation-expanded one
    setNavigating()
    setNavigationExpandedId(channelId)
  }

  const handleToggleClick = (channelId: string, isExpanded: boolean): void => {
    // Manual toggle via chevron: manage the manuallyExpandedIds set
    setManuallyExpandedIds((prev) => {
      const next = new Set(prev)
      if (isExpanded) {
        next.add(channelId)
      } else {
        next.delete(channelId)
      }
      return next
    })

    // If manually collapsing the navigation-expanded channel, clear it
    if (!isExpanded && navigationExpandedId === channelId) {
      setNavigationExpandedId(null)
    }
  }

  const handleReorder = async (
    dragIndex: number,
    hoverIndex: number,
  ): Promise<void> => {
    if (!currentOrganization?.id) return

    const draggedChannel = channels[dragIndex]
    if (!draggedChannel) return

    await orderChannel({
      organizationId: currentOrganization.id,
      channelId: draggedChannel.id,
      dragIndex,
      hoverIndex,
      channels,
    })
  }

  const handleDragStart = (channelId: string): void => {
    // Collapse the channel being dragged to reduce visual clutter during drag
    setManuallyExpandedIds((prev) => {
      const next = new Set(prev)
      next.delete(channelId)
      return next
    })
    if (navigationExpandedId === channelId) {
      setNavigationExpandedId(null)
    }
  }

  // When there are 0-1 channels, render without drag functionality
  if (!isDraggingEnabled) {
    return (
      <Sidebar.List aria-label="Channels" className={styles.channelsList}>
        {channels.map((channel) => (
          <li key={channel.id} className={styles.channelListItem}>
            <ChannelItem
              channel={channel}
              isExpanded={isChannelExpanded(channel.id)}
              handleNavigation={handleNavigation}
              onToggleClick={handleToggleClick}
              preventTransitions={isNavigating}
            />
          </li>
        ))}
      </Sidebar.List>
    )
  }

  // When there are 2+ channels, enable drag-and-drop
  return (
    <DraggableChannelsProvider
      channels={channels}
      onReorder={handleReorder}
      onDragStart={handleDragStart}
      disabled={false}
    >
      <DraggableChannelsList
        channels={channels}
        isChannelExpanded={isChannelExpanded}
        handleNavigation={handleNavigation}
        onToggleClick={handleToggleClick}
        isNavigating={isNavigating}
      />
    </DraggableChannelsProvider>
  )
}

/**
 * Inner component that consumes the dragging context to apply data-dragging attribute.
 * This component must be rendered within DraggableChannelsProvider.
 */
function DraggableChannelsList({
  channels,
  isChannelExpanded,
  handleNavigation,
  onToggleClick,
  isNavigating,
}: {
  channels: MainSidebarChannel[]
  isChannelExpanded: (channelId: string) => boolean
  handleNavigation: (channelId: string) => void
  onToggleClick: (channelId: string, isExpanded: boolean) => void
  isNavigating: boolean
}): JSX.Element {
  const { isDragging } = useDraggableChannelsContext()

  return (
    <Sidebar.List
      aria-label="Channels"
      className={styles.channelsList}
      data-dragging={isDragging || undefined}
    >
      {channels.map((channel) => (
        <DraggableChannelItem key={channel.id} channel={channel}>
          {(dragHandle) => (
            <ChannelItem
              channel={channel}
              isExpanded={isChannelExpanded(channel.id)}
              handleNavigation={handleNavigation}
              onToggleClick={onToggleClick}
              preventTransitions={isNavigating}
              dragHandle={dragHandle}
            />
          )}
        </DraggableChannelItem>
      ))}
    </Sidebar.List>
  )
}
